// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Pure presentational analysis-result view — no behavior change.
import React from 'react';

export default function AugmentedMETView({ data }) {
  if (!data) return null;
  if (data.refused) {
    return (
      <div className="space-y-5" data-testid="augmented-met-refused">
        <div className="bg-[#FDE7E7] border-l-4 border-[#B11F1F] px-5 py-4 rounded-md">
          <div className="font-bold text-[#7A1717] text-sm uppercase tracking-wider mb-1">Augmented MET — Scenario B (Refused)</div>
          <p className="text-[13px] text-[#2C2A28] leading-relaxed">{data.message}</p>
        </div>
        {data.detection && (
          <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-lg px-4 py-3 text-[12px]">
            <div className="font-semibold text-[#6E6B66] uppercase tracking-wider text-[10px] mb-2">Detection diagnostics</div>
            <div>Locations: <span className="font-mono">{data.detection.n_locations}</span> · Years: <span className="font-mono">{data.detection.n_years}</span> · Environments: <span className="font-mono">{data.detection.n_environments}</span></div>
            {data.detection.pct_test_genotypes_in_multiple_envs !== undefined && (
              <div className="mt-1">Test genotypes appearing in ≥ 2 environments: <span className="font-mono">{data.detection.pct_test_genotypes_in_multiple_envs}%</span></div>
            )}
          </div>
        )}
      </div>
    );
  }

  const pill = data.cross_validation_pill || {};
  const ds = data.design_summary || {};

  // ── Scenario B — germplasm screening (Federer check-anchored
  // combining over sites). Different render path from Scenario A:
  // NO G×E ANOVA / variance components; environment main effects
  // (from checks) + check-adjusted test ranking on a common scale.
  if (data.scenario === 'B') {
    const envEffects = data.env_effects || [];
    const ranking = data.scenario_b_ranking || [];
    const blupRanking = data.scenario_b_blup_ranking || [];
    return (
      <div className="space-y-6" data-testid="augmented-met-scenario-b-view">
        {data.provenance_banner && (
          <div className="bg-[#EAF4FB] border-l-4 border-[#2980B9] px-4 py-3 rounded-md" data-testid="met-b-banner">
            <p className="text-[12px] text-[#1F4E62] leading-relaxed">{data.provenance_banner}</p>
          </div>
        )}

        {/* G×E-not-estimable caveat (defense-in-depth) */}
        {data.gxe_caveat && (
          <div className="bg-[#FEF3C7] border-l-4 border-[#D97706] px-4 py-3 rounded-md" data-testid="met-b-gxe-caveat">
            <div className="font-bold text-[#92400E] text-[11px] uppercase tracking-wider mb-1">G × E Not Estimable — Screening Design</div>
            <p className="text-[12px] text-[#78350F] leading-relaxed">{data.gxe_caveat}</p>
          </div>
        )}

        {/* Design summary */}
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="met-b-design-summary">
          <div className="bg-[#F4FAF5] px-4 py-2 border-b border-[#E8E6E1] flex items-center justify-between">
            <h4 className="font-semibold text-sm text-[#1F4E2E] uppercase tracking-wider">Scenario B Design Summary</h4>
            {data.grand_check_mean !== undefined && data.grand_check_mean !== null && (
              <span className="text-[11px] text-[#6E6B66]">Grand check mean = <span className="font-mono font-bold text-[#2C2A28]" data-testid="met-b-grand-check-mean">{data.grand_check_mean}</span></span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 text-[12px]">
            {[
              ['Genotypes', ds.n_genotypes],
              ['Checks (common)', ds.n_checks],
              ['Tests (unreplicated)', ds.n_tests],
              ['Locations', ds.n_locations],
              ['Years', ds.n_years],
              ['Environments', ds.n_environments],
              ['Observations', ds.n_observations],
              ['Balanced', ds.balanced ? 'Yes' : 'No (screening)'],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">{k}</div>
                <div className="font-mono font-bold text-[#2C2A28]">{v ?? '-'}</div>
              </div>
            ))}
          </div>
          {ds.balance_note && (
            <p className="px-4 pb-3 text-[11px] text-[#6E6B66] italic">{ds.balance_note}</p>
          )}
        </div>

        {/* Environment main effects (from the common checks) */}
        {envEffects.length > 0 && (
          <div className="overflow-x-auto" data-testid="met-b-env-effects">
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Environment Main Effects (from Common Checks)</h4>
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#2980B9] text-white">
                <th className="px-3 py-2 text-left">Environment</th>
                <th className="px-3 py-2 text-center">Check Mean</th>
                <th className="px-3 py-2 text-center">Env Effect</th>
                <th className="px-3 py-2 text-center"># Checks</th>
                <th className="px-3 py-2 text-center"># Tests</th>
              </tr></thead>
              <tbody>
                {envEffects.map((e, i) => (
                  <tr key={e.Env} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F1F8FC]'}>
                    <td className="px-3 py-1.5 font-medium">{e.Env}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{e.env_check_mean}</td>
                    <td className="px-3 py-1.5 text-center font-mono">
                      <span className={e.env_effect > 0 ? 'text-emerald-700' : e.env_effect < 0 ? 'text-red-700' : ''}>
                        {e.env_effect > 0 ? '+' : ''}{e.env_effect}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-center">{e.n_checks}</td>
                    <td className="px-3 py-1.5 text-center">{e.n_tests}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Check-adjusted test ranking (combining over sites) */}
        {ranking.length > 0 && (
          <div className="overflow-x-auto" data-testid="met-b-ranking">
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">
              Check-Adjusted Test Ranking (Combining Over Sites)
            </h4>
            <p className="text-[11px] text-[#6E6B66] mb-2">
              Every test entry placed on one common, environment-corrected scale via
              Federer's check anchoring. Showing top {Math.min(25, ranking.length)} of {ranking.length}.
            </p>
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#16A085] text-white">
                <th className="px-3 py-2 text-center">Rank</th>
                <th className="px-3 py-2 text-left">Genotype</th>
                <th className="px-3 py-2 text-left">Environment</th>
                <th className="px-3 py-2 text-center">Raw Mean</th>
                <th className="px-3 py-2 text-center">Adjusted Mean</th>
              </tr></thead>
              <tbody>
                {ranking.slice(0, 25).map((r, i) => (
                  <tr key={r.Genotype} className={i % 2 === 0 ? 'bg-white' : 'bg-[#EBF7F3]'}>
                    <td className="px-3 py-1.5 text-center font-bold">{r.Rank}</td>
                    <td className="px-3 py-1.5 font-medium">{r.Genotype}</td>
                    <td className="px-3 py-1.5 text-[#6E6B66]">{r.Env}</td>
                    <td className="px-3 py-1.5 text-center font-mono text-[#8A8580]">{r.Raw_Mean}</td>
                    <td className="px-3 py-1.5 text-center font-mono font-bold text-[#2C2A28]">{r.CrossEnv_Adjusted_Mean}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Optional BLUP ranking (borrows strength through check network) */}
        {blupRanking.length > 0 && (
          <div className="overflow-x-auto" data-testid="met-b-blup-ranking">
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Mixed-Model BLUP Ranking (Test Entries)</h4>
            {data.scenario_b_blup_note && (
              <p className="text-[11px] text-[#6E6B66] mb-2 italic">{data.scenario_b_blup_note}</p>
            )}
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#4F46E5] text-white">
                <th className="px-3 py-2 text-center">BLUP Rank</th>
                <th className="px-3 py-2 text-left">Genotype</th>
                <th className="px-3 py-2 text-center">BLUP</th>
              </tr></thead>
              <tbody>
                {blupRanking.slice(0, 25).map((r, i) => (
                  <tr key={r.Genotype} className={i % 2 === 0 ? 'bg-white' : 'bg-[#EFEEFB]'}>
                    <td className="px-3 py-1.5 text-center font-bold">{r.BLUP_Rank}</td>
                    <td className="px-3 py-1.5 font-medium">{r.Genotype}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{r.BLUP}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Method note */}
        {data.method_note && (
          <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-lg px-4 py-3" data-testid="met-b-method-note">
            <div className="text-[10px] uppercase tracking-wider text-[#8A8580] font-semibold mb-1">Method</div>
            <p className="text-[11px] text-[#6E6B66] leading-relaxed">{data.method_note}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="augmented-met-view">
      {/* Provenance banner */}
      {data.provenance_banner && (
        <div className="bg-[#EAF4FB] border-l-4 border-[#2980B9] px-4 py-3 rounded-md" data-testid="met-banner">
          <p className="text-[12px] text-[#1F4E62] leading-relaxed">{data.provenance_banner}</p>
        </div>
      )}

      {/* Design summary panel */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="met-design-summary">
        <div className="bg-[#F4FAF5] px-4 py-2 border-b border-[#E8E6E1]">
          <h4 className="font-semibold text-sm text-[#1F4E2E] uppercase tracking-wider">MET Design Summary</h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 text-[12px]">
          {[
            ['Genotypes', ds.n_genotypes],
            ['Checks', ds.n_checks],
            ['Tests', ds.n_tests],
            ['Locations', ds.n_locations],
            ['Years', ds.n_years],
            ['Environments', ds.n_environments],
            ['Observations', ds.n_observations],
            ['Balanced', ds.balanced ? 'Yes' : 'Partial'],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">{k}</div>
              <div className="font-mono font-bold text-[#2C2A28]">{v ?? '-'}</div>
            </div>
          ))}
        </div>
        {ds.balance_note && (
          <p className="px-4 pb-3 text-[11px] text-[#6E6B66] italic">{ds.balance_note}</p>
        )}
      </div>

      {/* Cross-validation consensus pill */}
      {pill.verdict && (
        <div
          className="rounded-2xl px-5 py-4 border-l-4"
          style={{ background: `${pill.color}15`, borderColor: pill.color }}
          data-testid="met-cv-pill"
        >
          <div className="flex items-center gap-3 mb-1">
            <span className="font-bold text-sm uppercase tracking-wider" style={{ color: pill.color }}>
              Cross-Validation Pill — {pill.verdict}
            </span>
            <span className="font-mono text-[13px] text-[#2C2A28]">ρ = {pill.spearman_rho}</span>
          </div>
          <p className="text-[12px] text-[#2C2A28] leading-relaxed">{pill.message}</p>
        </div>
      )}

      {/* Combined ANOVA */}
      {data.anova_table && data.anova_table.length > 0 && (
        <div className="overflow-x-auto" data-testid="met-anova">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Combined ANOVA (Path 1 — Fixed Effects)</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#2980B9] text-white">
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-center">SS</th>
              <th className="px-3 py-2 text-center">DF</th>
              <th className="px-3 py-2 text-center">MS</th>
              <th className="px-3 py-2 text-center">F</th>
              <th className="px-3 py-2 text-center">P</th>
              <th className="px-3 py-2 text-center">Sig</th>
            </tr></thead>
            <tbody>
              {data.anova_table.map((r, i) => (
                <tr key={r.Source} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F1F8FC]'}>
                  <td className="px-3 py-1.5 font-medium">{r.Source}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.SS}</td>
                  <td className="px-3 py-1.5 text-center">{r.DF}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.MS ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.F ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.P ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center"><span className={`font-bold ${r.Sig === '**' ? 'text-green-700' : r.Sig === '*' ? 'text-amber-700' : 'text-gray-500'}`}>{r.Sig}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Variance components panel (Path 2 — Mixed Model) */}
      {data.variance_components && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="met-variance-components">
          <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
            <div className="bg-[#F0FAF6] px-4 py-2 border-b border-[#E8E6E1]">
              <h4 className="font-semibold text-sm text-[#16A085] uppercase tracking-wider">Variance Components (Path 2 — REML)</h4>
            </div>
            <table className="w-full text-xs border-collapse">
              <tbody>
                {data.variance_components.map((vc, i) => (
                  <tr key={vc.Component} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF6]'}>
                    <td className="px-3 py-2 text-[#2C2A28]">{vc.Component}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{vc.Estimate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
            <div className="bg-[#F0FAF6] px-4 py-2 border-b border-[#E8E6E1]">
              <h4 className="font-semibold text-sm text-[#16A085] uppercase tracking-wider">Heritability + Interaction Strength</h4>
            </div>
            <div className="p-4 space-y-2 text-[12px]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">H² across environments
                  <span className="ml-1 text-[9px] normal-case text-[#A09B95]">(test-entry mean basis, Piepho-Möhring 2007)</span>
                </div>
                <div className="font-mono font-bold text-[18px] text-[#2C2A28]" data-testid="h2-piepho-mohring">{data.H2_across_envs}</div>
              </div>
              {data.H2_cullis !== null && data.H2_cullis !== undefined && (
                <div data-testid="h2-cullis-block">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">H² Cullis (PEV-based, generalized)
                    <span className="ml-1 text-[9px] normal-case text-[#A09B95]">(Cullis 2006, rigorous for unbalanced trials)</span>
                  </div>
                  <div className="font-mono font-bold text-[18px] text-[#2C2A28]" data-testid="h2-cullis-value">
                    {data.H2_cullis}
                  </div>
                </div>
              )}
              {data.h2_divergence_band && (
                (() => {
                  const band = data.h2_divergence_band;
                  const palette = {
                    agreed: { bg: '#F2F0EC', fg: '#4F4C49', border: '#D6D3CE', label: 'Cullis ≈ Piepho-Möhring' },
                    mildly_divergent: { bg: '#FEF3C7', fg: '#92400E', border: '#D97706', label: 'Mildly divergent' },
                    strongly_divergent: { bg: '#FEE2E2', fg: '#991B1B', border: '#DC2626', label: 'Strongly divergent — quote Cullis' },
                  };
                  const p = palette[band.band] || palette.agreed;
                  return (
                    <div
                      className="rounded-lg px-3 py-2 mt-1"
                      style={{ background: p.bg, border: `1px solid ${p.border}`, color: p.fg }}
                      data-testid={`h2-divergence-pill-${band.band}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] font-semibold uppercase tracking-wider">
                          {p.label}
                        </span>
                        <span className="font-mono text-[11px]">
                          |Δ| = {band.gap}
                        </span>
                      </div>
                      <div className="text-[11px] mt-1" data-testid="h2-divergence-message">
                        {band.message}
                      </div>
                    </div>
                  );
                })()
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">σ²_ge / σ²_g (interaction strength)</div>
                <div className="font-mono font-bold text-[14px] text-[#2C2A28]">
                  {data.sigma2_ge_to_g_ratio ?? '-'}
                  {data.sigma2_ge_to_g_ratio !== null && data.sigma2_ge_to_g_ratio !== undefined && (
                    <span className="ml-2 text-[11px] font-normal text-[#6E6B66]">
                      {data.sigma2_ge_to_g_ratio < 0.5 ? '· small (broad adaptation realistic)' :
                       data.sigma2_ge_to_g_ratio < 1.5 ? '· moderate (specific-adaptation worth considering)' :
                       '· dominant (mega-environment-specific recommendations advised)'}
                    </span>
                  )}
                </div>
              </div>
              {data.sigma2_g_at_boundary && (
                <div className="bg-[#FDE7E7] border border-[#B11F1F] rounded-lg px-3 py-2 text-[11px] text-[#7A1717]" data-testid="sigma2-g-boundary-warning">
                  ⚠ σ²_g is at the boundary (≈ 0). H² estimates are unreliable; verify the data structure has genuine across-genotype variation.
                </div>
              )}
              {data.sigma2_ge_at_boundary && !data.sigma2_g_at_boundary && (
                <div className="bg-[#FEF3C7] border border-[#D97706] rounded-lg px-3 py-2 text-[11px] text-[#92400E]" data-testid="sigma2-ge-boundary-warning">
                  ⓘ σ²_ge is at the boundary (≈ 0). G×E variance is undetectable in this dataset — rankings are stable across environments and a single consensus recommendation is appropriate.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Combined side-by-side genotype table */}
      {data.combined_table && data.combined_table.length > 0 && (
        <div className="overflow-x-auto" data-testid="met-combined-table">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Side-by-Side: Path 1 Adjusted Mean vs Path 2 BLUP</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#4F46E5] text-white">
              <th className="px-3 py-2 text-left">Genotype</th>
              <th className="px-3 py-2 text-center">Type</th>
              <th className="px-3 py-2 text-center">Path 1 Adj. Mean</th>
              <th className="px-3 py-2 text-center">Path 1 Rank</th>
              <th className="px-3 py-2 text-center">Path 2 BLUP</th>
              <th className="px-3 py-2 text-center">Path 2 Rank</th>
              <th className="px-3 py-2 text-center">Rank Δ</th>
              <th className="px-3 py-2 text-center">Shrinkage</th>
            </tr></thead>
            <tbody>
              {data.combined_table.map((r, i) => (
                <tr key={r.Genotype} className={i % 2 === 0 ? 'bg-white' : 'bg-[#EFEEFB]'}>
                  <td className="px-3 py-1.5 font-medium">{r.Genotype}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${r.IsCheck === 1 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {r.IsCheck === 1 ? 'check' : 'test'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.Path1_Adjusted_Mean ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center">{r.Path1_Rank ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.Path2_BLUP ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center">{r.Path2_Rank ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center">
                    {r.Rank_Difference !== null && r.Rank_Difference !== undefined ? (
                      <span className={`font-mono ${Math.abs(r.Rank_Difference) <= 1 ? 'text-green-700' : Math.abs(r.Rank_Difference) <= 3 ? 'text-amber-700' : 'text-red-700 font-bold'}`}>
                        {r.Rank_Difference > 0 ? '+' : ''}{r.Rank_Difference}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-1.5 text-center font-mono text-[#6E6B66]">{r.Shrinkage ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Figures */}
      {data.gxe_interaction_chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="met-gxe-chart">
          <div className="bg-[#2980B9]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#2980B9] uppercase tracking-wider">G × E Interaction Plot</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">One line per genotype across environments. Solid = test, dashed = check. Crossing lines = G×E.</p>
          </div>
          <img src={`data:image/png;base64,${data.gxe_interaction_chart}`} alt="GxE Interaction" className="w-full" />
        </div>
      )}
      {data.ranking_scatter_chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="met-ranking-chart">
          <div className="bg-[#4F46E5]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#4F46E5] uppercase tracking-wider">Cross-Validation: Path 1 vs Path 2 Ranking</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">Diagonal = perfect agreement. Off-diagonal points = method disagreement.</p>
          </div>
          <img src={`data:image/png;base64,${data.ranking_scatter_chart}`} alt="Ranking scatter" className="w-full" />
        </div>
      )}

      {/* Interpretation */}
      {data.interpretation && (
        <div className="bg-[#FFFBEB] border-l-4 border-[#F39C12] px-4 py-3 rounded-md" data-testid="met-interpretation">
          <p className="text-[12px] text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Citations */}
      {data.citations && data.citations.length > 0 && (
        <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-lg px-4 py-3" data-testid="met-citations">
          <div className="text-[10px] uppercase tracking-wider text-[#8A8580] font-semibold mb-2">References</div>
          <ul className="space-y-1 text-[11px] text-[#6E6B66] list-disc list-inside">
            {data.citations.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
